// src/components/collections/Collection_items.js

/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { collection, getDocs } from "firebase/firestore";
import CategoryItem from "./CategoryItem";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { db } from "../../config/fireabseConfig"; // Ensure correct path
import { HeadLine } from "../component";

const Collection_items = () => {
  const [categories, setCategories] = useState([]);
  const [elements, setElements] = useState([]);
  const [filteredElements, setFilteredElements] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingElements, setLoadingElements] = useState(true);

  useEffect(() => {
    // Fetch categories from Firebase
    const fetchCategories = async () => {
      try {
        const categoryCollection = collection(db, "elementCategories");
        const categorySnapshot = await getDocs(categoryCollection);
        const fetchedCategories = categorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };

    // Fetch elements from Firebase
    const fetchElements = async () => {
      try {
        const elementsCollection = collection(db, "elements");
        const elementsSnapshot = await getDocs(elementsCollection);
        const fetchedElements = elementsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setElements(fetchedElements);
        setFilteredElements(fetchedElements); // Initially show all
      } catch (error) {
        console.error("Error fetching elements:", error);
      } finally {
        setLoadingElements(false);
      }
    };

    fetchCategories();
    fetchElements();
  }, []);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredElements(elements);
    } else {
      const filtered = elements.filter(
        (element) => element.categoryId === activeCategory
      );
      setFilteredElements(filtered);
    }
  }, [activeCategory, elements]);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <>
      <section className="relative py-12 bg-gray-50 dark:bg-jacarta-900">
        <picture className="pointer-events-none absolute inset-0 -z-10">
          <Image
            src="/images/gradient_light.jpg"
            alt="gradient"
            className="h-full w-full"
            layout="fill"
            objectFit="cover"
            priority
          />
        </picture>
        <div className="container mx-auto px-4">
          {/* Headline */}
          <HeadLine
            title="Our Collections"
            subtitle="Explore our extensive range"
          />

          {/* Slider for Categories using Swiper */}
          <div className="mb-12">
            {loadingCategories ? (
              // Loading Skeleton for Categories
              <Swiper
                modules={[Navigation, Pagination, Scrollbar, A11y]}
                spaceBetween={25}
                slidesPerView={5}
                navigation
                pagination={{ clickable: true }}
                scrollbar={{ draggable: true }}
                breakpoints={{
                  320: {
                    slidesPerView: 2,
                    spaceBetween: 15,
                  },
                  640: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 25,
                  },
                  1280: {
                    slidesPerView: 5,
                    spaceBetween: 30,
                  },
                }}
              >
                {[...Array(5)].map((_, index) => (
                  <SwiperSlide key={index}>
                    <div className="p-4 flex flex-col items-center gap-3 border border-gray-200 rounded-lg shadow-sm animate-pulse bg-white dark:bg-gray-800">
                      <div className="w-14 h-14 bg-gray-300 rounded-full"></div>
                      <div className="w-24 h-4 bg-gray-300 rounded"></div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Scrollbar, A11y]}
                spaceBetween={25}
                slidesPerView={5}
                navigation
                pagination={{ clickable: true }}
                // scrollbar={{ draggable: true }}
                breakpoints={{
                  320: {
                    slidesPerView: 2,
                    spaceBetween: 15,
                  },
                  640: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 25,
                  },
                  1280: {
                    slidesPerView: 5,
                    spaceBetween: 30,
                  },
                }}
              >
                {/* All Categories */}
                <SwiperSlide>
                  <div
                    className={`p-4 flex flex-col items-center gap-3 cursor-pointer border-2 rounded-lg transition-colors duration-300 ${
                      activeCategory === "all"
                        ? "border-green-500 bg-green-100 text-green-700"
                        : "border-gray-300 bg-white hover:bg-green-50 dark:bg-jacarta-800 dark:border-jacarta-700"
                    }`}
                    onClick={() => handleCategoryClick("all")}
                  >
                    <div className="w-14 h-14 bg-green-200 rounded-full flex items-center justify-center">
                      <span className="text-lg font-semibold">All</span>
                    </div>
                  </div>
                </SwiperSlide>

                {/* Dynamically Rendered Categories */}
                {categories.map((category) => (
                  <SwiperSlide key={category.id}>
                    <div
                      className={`p-4 flex flex-row items-center gap-3 cursor-pointer border-2 rounded-lg transition-colors duration-300 ${
                        activeCategory === category.id
                          ? "border-green-500 bg-green-100 text-green-700"
                          : "border-gray-300 bg-white hover:bg-green-50 dark:bg-jacarta-800 dark:border-jacarta-700"
                      }`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <Image
                        src={category.imageUrl}
                        alt={category.title}
                        width={56}
                        height={56}
                        className="object-contain "
                        unoptimized
                      />
                      <span
                        className={`text-sm font-medium ${
                          activeCategory === category.id
                            ? "text-green-600"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {category.title}
                      </span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>

          {/* Elements Grid */}
          {loadingElements ? (
            // Loading Skeleton for Elements
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="dark:bg-gray-800 bg-white rounded-lg shadow-md overflow-hidden animate-pulse"
                >
                  <div className="w-full h-60 bg-gray-300"></div>
                  <div className="p-4 space-y-2">
                    <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-300 rounded"></div>
                  </div>
                  <div className="px-4 pb-4 flex justify-between items-center">
                    <div className="h-4 bg-gray-300 rounded w-24"></div>
                    <div className="h-4 bg-gray-300 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <CategoryItem items={filteredElements} />
          )}
        </div>
      </section>
    </>
  );
};

export default Collection_items;
